import { useApolloClient } from '@apollo/client';
import { useSession } from 'providers';
import { useSessionExpiryModal } from 'providers/SessionExpiryModalProvider/SessionExpiryModalProvider';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logError } from 'utils/logError';
import { isSessionExpired } from 'utils/sessionExpiry';
import { useLogout } from '../hooks/useLogout';

// 30 seconds
const EXPIRATION_CHECK_FREQUENCY = 1000 * 30;

export const useSessionExpiration = () => {
  const apolloClient = useApolloClient();
  const { user, clearSessionUser, getExpiresIn, firebaseId, sessionState } =
    useSession();
  const { isModalOpen, openSessionExpiryModal, closeSessionExpiryModal } =
    useSessionExpiryModal();
  const { pathname } = useLocation();
  const { expireSession } = useLogout();

  const expiresIn = getExpiresIn();
  const isLoginPage = pathname === '/';

  // If the user is not set after 5 seconds, show the sign in modal
  useEffect(() => {
    if (!user && !isLoginPage && sessionState !== SESSION_STATE.LOGGING_OUT) {
      const timeoutId = setTimeout(() => openSessionExpiryModal(), 5000);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [user, sessionState, isLoginPage, openSessionExpiryModal]);

  useEffect(() => {
    if (isLoginPage && isModalOpen) {
      closeSessionExpiryModal();
    }
  }, [isLoginPage, isModalOpen, closeSessionExpiryModal]);

  // Check if session is expired on a set interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!isModalOpen && isSessionExpired(expiresIn) && firebaseId) {
        if (isLoginPage) {
          clearSessionUser().catch((error: unknown) => {
            logError({ error });
          });
        }

        void expireSession();

        if (!isModalOpen && !isLoginPage) {
          openSessionExpiryModal();
        }

        void apolloClient.clearStore();
      }
    }, EXPIRATION_CHECK_FREQUENCY);

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseId, expiresIn, isModalOpen, isLoginPage]);
};
